import { useCallback, useRef, useState } from 'react'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import type { Task } from '@/types/task'
import type { CalendarEvent } from '@/hooks/useGoogleCalendar'
import type { Routine } from '@/types/actionable'
import { localYmd } from '@/lib/cadence/config'
import { showToast } from '@/hooks/useToast'
import { findEventByItemId, eventItemKey } from '@/types/timeline'
import { timingRemoval } from '@/lib/planning/planActions'

// Task updates may include endTime (timed duration) even though it's not yet
// on the base Task type — the DB layer accepts it via the update handler.
type TaskUpdates = Partial<Task> & { endTime?: Date }

interface UseWeekDragDropArgs {
  weekStart: Date
  onWeekChange: (newWeekStart: Date) => void
  onUpdateTask: (taskId: string, updates: TaskUpdates) => Promise<void | boolean> | void
  /**
   * Move an event. OPTIONAL on purpose, and absent means "this host cannot
   * move events" — which is said out loud. It used to be required and hosts
   * satisfied it with a no-op, so a drag announced a move that never happened
   * (Scott's walkthrough, 2026-09-24).
   *
   * It must REJECT when the write fails; the confirmation and the Undo wait
   * on it.
   */
  onUpdateEvent?: (eventId: string, updates: { startTime: Date; endTime: Date }) => Promise<void> | void
  onUpdateRoutine: (routineId: string, updates: Partial<Routine>) => Promise<void> | void
  tasks: (Task & { endTime?: Date })[]
  events: CalendarEvent[]
  routines: Routine[]
  /** Number of days in the displayed week — controls cross-week auto-advance step. Default 7. */
  dayCount?: number
  /** Optional. Called after a successful drag mutation to surface an undo toast. */
  pushAction?: (message: string, undo: () => void) => void
  /** Optional. Pin a routine to a time on ONE day (a deferral/override write),
   *  leaving its recurrence rule alone — the same grain rule as the Today
   *  overlay's routine drop. Omitted = routine blocks don't move. */
  onPushRoutine?: (routineId: string, when: Date, fromDate: Date) => void
  /** Optional. A shelf routine pill dropped on a slot ASKS before writing —
   *  the host opens the place-scope popover ("every Thursday" vs "just this
   *  Thursday"). The hook never writes routines on this path itself. */
  onRoutinePlaceRequest?: (req: { routineId: string; when: Date }) => void
  /** A month line dropped on the week's list: the week's own "+ This week". */
  onTakeIn?: (taskId: string) => void
  /** A routine occurrence (or a "Sometime this weekend" routine) dropped on a
   *  day: the host moves THAT occurrence, never the rule. */
  onRoutineToDay?: (routineId: string, fromIso: string, toIso: string, title: string) => void
}

interface UseWeekDragDropResult {
  dndHandlers: {
    onDragStart: (e: DragStartEvent) => void
    onDragEnd: (e: DragEndEvent) => void
    onDragCancel: () => void
  }
  activeDragId: string | null
  /** Call when the dragged pointer enters/leaves an edge zone. Null clears. */
  notifyEdge: (edge: 'left' | 'right' | null) => void
}

const DEFAULT_DURATION_MS = 30 * 60 * 1000

/** Is this grid day (local YYYY-MM-DD) before today? */
function isPastDay(dayIso: string): boolean {
  return dayIso < localYmd(new Date())
}

export function useWeekDragDrop(args: UseWeekDragDropArgs): UseWeekDragDropResult {
  const [activeDragId, setActiveDragId] = useState<string | null>(null)

  const onDragStart = useCallback((e: DragStartEvent) => {
    setActiveDragId(String(e.active.id))
  }, [])

  const onDragCancel = useCallback(() => {
    setActiveDragId(null)
  }, [])

  /**
   * Every render's args, for handlers that must NOT be rebuilt on each one.
   *
   * `onDragEnd` depended on `[tasks, onUpdateTask]` while reading `events`,
   * `onUpdateEvent`, `weekStart` and `pushAction` — so whenever the events
   * array changed identity without those two changing, the handler kept
   * looking things up in the PREVIOUS array. Live, that meant the first event
   * drag of a session worked and the second said "couldn't find that event":
   * a successful move refetches the range, which replaces `events`
   * (2026-09-24, found while verifying the id-lookup repair).
   */
  const argsRef = useRef(args)
  argsRef.current = args

  const onDragEnd = useCallback((e: DragEndEvent) => {
    const a = argsRef.current
    const { tasks, onUpdateTask } = a
    setActiveDragId(null)
    if (!e.over) return

    const activeData = e.active.data.current as
      | { kind?: string; taskId?: string; itemId?: string; routineId?: string; keepTime?: boolean; fromIso?: string; title?: string; fromSometime?: boolean }
      | undefined
    const overData = e.over.data.current as
      | { kind?: string; dayIso?: string; hour?: number; minute?: number }
      | undefined

    if (!activeData || !overData) return

    // A pool chip dropped on a day that has already passed lands "expired"
    // the instant it is written — Today's attention queue, the Start-the-day
    // sheet and Inbox's Expired section all read a past date as slid. (Demo
    // walkthrough 2026-09-04: a fresh "this week" task dragged onto the
    // current week's Sunday read as "slid 5d" everywhere.) Refuse the drop
    // and say why; a block already on the grid may still be moved anywhere.
    if ((activeData.kind === 'chip' || activeData.kind === 'refLine' || activeData.kind === 'routineOcc') && overData.dayIso && isPastDay(overData.dayIso)) {
      showToast('That day has passed — drop it on today or later', 'warning')
      return
    }

    // One drag rule (Scott, 2026-10-03): anything that can go somewhere else
    // drags there. A routine occurrence moves as that one occurrence — the
    // rule is never rewritten by a drag.
    if (activeData.kind === 'routineOcc' && activeData.routineId && activeData.fromIso) {
      if (overData.kind === 'weekList') {
        showToast('Routines repeat on their own schedule — they aren’t added to a list', 'warning')
        return
      }
      // A Sometime row carries Saturday as its day; a drop onto Saturday still
      // gives it Saturday (final review, 2026-10-03).
      if (overData.kind === 'allDay' && overData.dayIso && (activeData.fromSometime || overData.dayIso !== activeData.fromIso)) {
        a.onRoutineToDay?.(activeData.routineId, activeData.fromIso, overData.dayIso, activeData.title ?? 'routine')
      }
      return
    }

    // A month line: onto the list is the week's "+ This week"; onto a day,
    // it is this week's, on that day, any time.
    if (activeData.kind === 'refLine' && activeData.taskId) {
      const task = tasks.find((t) => t.id === activeData.taskId)
      if (overData.kind === 'weekList') { a.onTakeIn?.(activeData.taskId); return }
      if (overData.kind === 'allDay' && overData.dayIso && task) {
        const prev = { bucket: task.bucket, isAllDay: task.isAllDay ?? false, scheduledFor: task.scheduledFor as Date, weekStart: task.weekStart as Date }
        void onUpdateTask(task.id, { bucket: 'timed', isAllDay: true, scheduledFor: parseSlotTime(overData.dayIso, 0, 0), weekStart: a.weekStart })
        a.pushAction?.(`“${task.title}” → ${parseSlotTime(overData.dayIso, 0, 0).toLocaleDateString('en-US', { weekday: 'long' })}`, () => { void onUpdateTask(task.id, prev) })
      }
      return
    }

    // Dropped back on the week's list (v2): off its day, still this week —
    // "Any day" again. The day comes off the way the timing menu's "Remove
    // the day" takes it off; the week is (re)stated, so a task that was only
    // ever dated joins the list rather than falling to the Inbox.
    if (overData.kind === 'weekList') {
      const taskId =
        activeData.kind === 'chip'
          ? activeData.taskId
          : activeData.itemId?.startsWith('task-')
          ? activeData.itemId.slice('task-'.length)
          : undefined
      const task = taskId ? tasks.find((t) => t.id === taskId) : undefined
      if (!task || !task.scheduledFor) return
      const { updates, previous } = timingRemoval(task, 'day')
      void onUpdateTask(task.id, { ...updates, bucket: 'week', weekStart: a.weekStart })
      a.pushAction?.(`“${task.title}” is back on the week’s list`, () => {
        void onUpdateTask(task.id, { ...previous, bucket: task.bucket })
      })
      return
    }

    // Dropped on a day's all-day cell → schedule (or keep) the task as all-day
    // on that day. Without this, an all-day chip could only be dropped on a
    // time slot, so "move this to another day, still all-day" did nothing.
    if (overData.kind === 'allDay' && overData.dayIso) {
      const taskId =
        activeData.kind === 'chip'
          ? activeData.taskId
          : activeData.itemId?.startsWith('task-')
          ? activeData.itemId.slice('task-'.length)
          : undefined
      if (!taskId) return
      const task = tasks.find((t) => t.id === taskId)
      // A timed task keeps its clock time on its new day, and its length
      // (one drag rule, 2026-10-03); untimed work lands any time.
      if (activeData.keepTime && task?.scheduledFor && !task.isAllDay) {
        const from = task.scheduledFor
        const to = parseSlotTime(overData.dayIso, from.getHours(), from.getMinutes())
        const shift = to.getTime() - from.getTime()
        const prevEnd = task.endTime
        void onUpdateTask(taskId, { isAllDay: false, scheduledFor: to, bucket: 'timed', ...(prevEnd ? { endTime: new Date(prevEnd.getTime() + shift) } : {}) })
        a.pushAction?.(`Moved "${task.title}"`, () => {
          void onUpdateTask(taskId, { isAllDay: false, scheduledFor: from, bucket: task.bucket, ...(prevEnd ? { endTime: prevEnd } : {}) })
        })
        return
      }
      const newDay = parseSlotTime(overData.dayIso, 0, 0)
      const prevScheduledFor = task?.scheduledFor ?? null
      const prevIsAllDay = task?.isAllDay ?? false
      const prevEndTime = task?.endTime
      // bucket:'timed' rides along — a pool pill may start in week/month/inbox,
      // and a scheduled task must never sit in another bucket (timed-bucket
      // invariant). No-op for already-placed chips.
      const prevBucket = task?.bucket
      // A drag onto a day is a DATE and nothing else (spec S4): it never
      // chooses the task for anyone's focus. Dated work shows on its day's
      // Tasks without focus (S12).
      void onUpdateTask(taskId, { isAllDay: true, scheduledFor: newDay, bucket: 'timed' })
      a.pushAction?.(`Moved "${task?.title ?? 'task'}"`, () => {
        void onUpdateTask(taskId, {
          isAllDay: prevIsAllDay,
          scheduledFor: prevScheduledFor as Date,
          endTime: prevEndTime,
          bucket: prevBucket,
        })
      })
      return
    }

    if (overData.kind !== 'timed' || !overData.dayIso) return

    const newStart = parseSlotTime(overData.dayIso, overData.hour ?? 0, overData.minute ?? 0)

    // Shelf routine pill: the drop only ASKS — scope (rule vs one week) is
    // the host's popover decision, never an implicit write.
    if (activeData.kind === 'routineChip' && activeData.routineId) {
      a.onRoutinePlaceRequest?.({ routineId: activeData.routineId, when: newStart })
      return
    }

    if (activeData.kind === 'chip' && activeData.taskId) {
      const task = tasks.find((t) => t.id === activeData.taskId)
      const prevScheduledFor = task?.scheduledFor ?? null
      const prevIsAllDay = task?.isAllDay ?? false
      const prevEndTime = task?.endTime
      // bucket:'timed' rides along — a pool pill may start in week/month/inbox,
      // and a scheduled task must never sit in another bucket (timed-bucket
      // invariant). No-op for already-placed chips.
      const prevBucket = task?.bucket
      void onUpdateTask(activeData.taskId, {
        isAllDay: false,
        scheduledFor: newStart,
        endTime: new Date(newStart.getTime() + DEFAULT_DURATION_MS),
        bucket: 'timed',
      })
      a.pushAction?.(`Scheduled "${task?.title ?? 'task'}"`, () => {
        void onUpdateTask(activeData.taskId!, {
          isAllDay: prevIsAllDay,
          scheduledFor: prevScheduledFor as Date,
          endTime: prevEndTime,
          bucket: prevBucket,
        })
      })
      return
    }

    if (activeData.kind === 'block' && activeData.itemId) {
      const itemId = String(activeData.itemId)
      // Strip the type prefix to get the raw DB id, then route to the correct
      // mutator. TimelineItem.id is prefixed ('task-xyz', 'event-xyz', etc.);
      // the DB update functions expect the raw uuid.
      if (itemId.startsWith('routine-')) {
        // Strip the '-dayN' render suffix to recover the DB id (same rule as
        // WeekViewV2.handleSelectBlock). One-day override, never a rule
        // rewrite — one drag must not move every future occurrence.
        const rendered = itemId.slice('routine-'.length)
        const dayMatch = rendered.match(/-day(\d+)$/)
        const routineId = rendered.replace(/-day\d+$/, '')
        // The suffix is also the only record of which column the block was
        // dragged FROM. Without it the override lands on whatever day the rest
        // of the app happens to be viewing, which is how a drag on Thursday
        // silently rewrote Monday.
        const fromDate = new Date(a.weekStart)
        fromDate.setDate(fromDate.getDate() + (dayMatch ? Number(dayMatch[1]) : 0))
        a.onPushRoutine?.(routineId, newStart, fromDate)
        return
      }
      if (itemId.startsWith('task-')) {
        const taskId = itemId.slice('task-'.length)
        const task = tasks.find((t) => t.id === taskId)
        if (!task?.scheduledFor) return
        const oldStart = task.scheduledFor
        const oldEnd = task.endTime ?? new Date(oldStart.getTime() + DEFAULT_DURATION_MS)
        const duration = oldEnd.getTime() - oldStart.getTime()
        void onUpdateTask(taskId, {
          scheduledFor: newStart,
          endTime: new Date(newStart.getTime() + duration),
        })
        a.pushAction?.(`Moved "${task.title}"`, () => {
          void onUpdateTask(taskId, {
            scheduledFor: oldStart,
            endTime: oldEnd,
          })
        })
        return
      }
      if (itemId.startsWith('event-')) {
        // By the SAME rule that built the id. Matching `ev.id` here looked
        // right and never matched a real calendar event, so every drop
        // returned on the next line without a word (Scott, 2026-09-24).
        const event = findEventByItemId(a.events, itemId)
        if (!event) {
          showToast('Couldn’t find that event to move', 'error', 4000)
          return
        }
        const eventId = eventItemKey(event)
        const startStr =
          (event as { start_time?: string }).start_time ??
          (event as { startTime?: string }).startTime
        const endStr =
          (event as { end_time?: string }).end_time ??
          (event as { endTime?: string }).endTime
        if (!startStr || !endStr) return
        const oldStart = new Date(startStr)
        const oldEnd = new Date(endStr)
        const duration = oldEnd.getTime() - oldStart.getTime()
        const newEnd = new Date(newStart.getTime() + duration)
        const move = a.onUpdateEvent
        if (!move) {
          // No writer. Better to say so than to pretend it moved.
          showToast('Moving events isn’t available here', 'error', 4000)
          return
        }
        void (async () => {
          try {
            await move(eventId, { startTime: newStart, endTime: newEnd })
          } catch {
            // The writer has already said what went wrong. No confirmation
            // and no Undo for a move that did not happen.
            return
          }
          a.pushAction?.(`Moved "${event.title}"`, () => {
            void (async () => {
              try { await move(eventId, { startTime: oldStart, endTime: oldEnd }) } catch { /* reported by the writer */ }
            })()
          })
        })()
        return
      }
      // 'routine-...' shouldn't reach here (routines are non-draggable per spec).
      return
    }
  // Nothing from props: the handler reads this render's args through the ref
  // above, so it can never hold a stale events list again.
  }, [])

  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cooldownRef = useRef<boolean>(false)

  const notifyEdge = useCallback((edge: 'left' | 'right' | null) => {
    if (edge === null) {
      if (advanceTimerRef.current) {
        clearTimeout(advanceTimerRef.current)
        advanceTimerRef.current = null
      }
      return
    }
    if (cooldownRef.current) return
    if (advanceTimerRef.current) return // already armed
    advanceTimerRef.current = setTimeout(() => {
      const direction = edge === 'right' ? (args.dayCount ?? 7) : -(args.dayCount ?? 7)
      const newStart = new Date(args.weekStart)
      newStart.setDate(newStart.getDate() + direction)
      args.onWeekChange(newStart)
      advanceTimerRef.current = null
      cooldownRef.current = true
      setTimeout(() => { cooldownRef.current = false }, 300)
    }, 500)
  }, [args])

  return {
    dndHandlers: { onDragStart, onDragEnd, onDragCancel },
    activeDragId,
    notifyEdge,
  }
}

function parseSlotTime(dayIso: string, hour: number, minute: number): Date {
  const [y, m, d] = dayIso.split('-').map(Number)
  return new Date(y, m - 1, d, hour, minute, 0, 0)
}
